"use client";

import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import Script from "next/script";
import { type SchemaId } from "@/lib/faq-data";

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  title?: string;
  subtitle?: string;
  faqs: FAQItem[];
  schemaId: SchemaId;
}

/**
 * FAQ Section with Schema.org FAQPage JSON-LD markup.
 * Helps LLMs (ChatGPT, Claude, Gemini, Perplexity) extract questions and
 * answers to surface Miller40 in AI-generated responses.
 */
export function FAQSection({
  title = "Frequently Asked Questions",
  subtitle,
  faqs,
  schemaId,
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const reactId = useId();
  const idPrefix = `${schemaId}-${reactId}`;

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Build FAQPage schema for LLM visibility
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  // Escape < to prevent XSS via premature script tag termination
  const faqSchemaJson = JSON.stringify(faqSchema).replace(/</g, "\\u003c");

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-site-section">
      <Script
        id={schemaId}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: faqSchemaJson }}
      />

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-yellow-500 mb-4">
            {title}
          </h2>
          {subtitle && (
            <p className="text-lg text-site-foreground/70">
              {subtitle}
            </p>
          )}
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-site-card rounded-lg border border-white/10 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-inset"
                aria-expanded={openIndex === index}
                aria-controls={`${idPrefix}-faq-answer-${index}`}
              >
                <span className="text-base sm:text-lg font-semibold text-site-foreground pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-yellow-500 flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                />
              </button>
              <div
                id={`${idPrefix}-faq-answer-${index}`}
                role="region"
                className={`grid transition-[grid-template-rows,opacity] duration-200 ${
                  openIndex === index
                    ? "grid-rows-[1fr] opacity-100"
                    : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <div
                    className={`text-site-foreground/70 leading-relaxed ${
                      openIndex === index ? "px-5 sm:px-6 pb-5 sm:pb-6" : "px-5 sm:px-6 pb-0"
                    }`}
                  >
                    {faq.answer}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
